// ---------------------------------------------------------------------------
// Accounts and passwords: an organizer creates a volunteer or resets a
// forgotten password, and is shown a password to hand over.
//
// Nothing here holds a privilege the browser did not already have.
// create_account(), reset_password() and list_accounts() are security definer
// RPCs that check the caller's role themselves (20260927000100); the
// service_role key is not involved. The volunteer's half -- choosing their
// own password afterwards -- is an ordinary GoTrue updateUser on their own
// session.
// ---------------------------------------------------------------------------

import type { AccountDraft, PasswordResetDraft } from '@yucanvas/shared';
import { supabase } from '../lib/supabase';

/** The one sight of a temporary password. Stored only as a hash. */
export interface IssuedPassword {
  reason: 'created' | 'reset';
  name: string;
  email: string;
  password: string;
}

export interface Account {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  last_sign_in_at: string | null;
  password_issued_at: string | null;
  password_issued_by: string | null;
  password_issued_reason: 'created' | 'reset' | null;
  /** Given a password by an organizer, and not yet chosen their own. */
  change_pending: boolean;
}

export async function createAccount(draft: AccountDraft): Promise<IssuedPassword> {
  const { data, error } = await supabase
    .rpc('create_account', {
      p_email: draft.email,
      p_name: draft.name,
      p_role: draft.role,
    })
    .single();
  if (error) throw new Error(error.message);
  return {
    reason: 'created',
    name: draft.name,
    email: draft.email,
    password: data.account_password,
  };
}

export async function resetPassword(draft: PasswordResetDraft): Promise<IssuedPassword> {
  const { data, error } = await supabase.rpc('reset_password', {
    p_email: draft.email,
    p_name: draft.name,
  });
  if (error) throw new Error(error.message);
  return { reason: 'reset', name: draft.name, email: draft.email, password: data };
}

/** Admin only. Every account, with the email profiles does not carry. */
export async function fetchAccounts(): Promise<Account[]> {
  const { data, error } = await supabase.rpc('list_accounts');
  if (error) throw new Error(error.message);
  return (data ?? []) as Account[];
}

/**
 * Whether the signed-in user is still on a password somebody else was shown.
 *
 * A failure reads as false rather than locking the app: an RPC that is missing
 * -- the migration not pushed yet -- must not stop a canvasser working their
 * turf. The cost is that a reset volunteer skips the prompt until it is.
 */
export async function passwordChangeRequired(): Promise<boolean> {
  const { data, error } = await supabase.rpc('password_change_required');
  if (error) return false;
  return data === true;
}

export async function changeMyPassword(password: string): Promise<void> {
  const { error } = await supabase.auth.updateUser({ password });
  if (error) {
    // GoTrue's wording for the one mistake worth explaining.
    if (error.message.toLowerCase().includes('different from the old')) {
      throw new Error('Choose a new password, not the temporary one you were given.');
    }
    throw new Error(error.message);
  }
}
